import React, { useMemo, useState } from "react";
import { Bell, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Full US state list — matches backend US_STATES codes.
const US_STATES_FULL = [
  ["AL","Alabama"], ["AK","Alaska"], ["AZ","Arizona"], ["AR","Arkansas"], ["CA","California"],
  ["CO","Colorado"], ["CT","Connecticut"], ["DE","Delaware"], ["DC","District of Columbia"],
  ["FL","Florida"], ["GA","Georgia"], ["HI","Hawaii"], ["ID","Idaho"], ["IL","Illinois"],
  ["IN","Indiana"], ["IA","Iowa"], ["KS","Kansas"], ["KY","Kentucky"], ["LA","Louisiana"],
  ["ME","Maine"], ["MD","Maryland"], ["MA","Massachusetts"], ["MI","Michigan"], ["MN","Minnesota"],
  ["MS","Mississippi"], ["MO","Missouri"], ["MT","Montana"], ["NE","Nebraska"], ["NV","Nevada"],
  ["NH","New Hampshire"], ["NJ","New Jersey"], ["NM","New Mexico"], ["NY","New York"],
  ["NC","North Carolina"], ["ND","North Dakota"], ["OH","Ohio"], ["OK","Oklahoma"], ["OR","Oregon"],
  ["PA","Pennsylvania"], ["RI","Rhode Island"], ["SC","South Carolina"], ["SD","South Dakota"],
  ["TN","Tennessee"], ["TX","Texas"], ["UT","Utah"], ["VT","Vermont"], ["VA","Virginia"],
  ["WA","Washington"], ["WV","West Virginia"], ["WI","Wisconsin"], ["WY","Wyoming"],
];

const ROLE_OPTIONS = [
  { value: "shelter", label: "Shelter" },
  { value: "rescue", label: "Rescue" },
  { value: "animal_control", label: "Animal control / city agency" },
  { value: "foster_adopter", label: "Foster / adopter" },
  { value: "public", label: "Member of the public" },
];

export default function ActiveCaseAlertsForm() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("public");
  const [orgName, setOrgName] = useState("");
  const [selected, setSelected] = useState([]);  // array of state codes
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (code) => setSelected((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  const canSubmit = useMemo(() => !!email && selected.length > 0 && !submitting, [email, selected, submitting]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/active-case-alerts/subscribe", {
        email,
        states: selected,
        role,
        org_name: orgName.trim() || null,
      });
      setDone(true);
      toast.success("You're subscribed. Watch for alerts when new cases publish in your state.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border-2 border-emerald-700 bg-emerald-50 p-6" data-testid="active-case-alerts-subscribed">
        <div className="flex items-start gap-3">
          <Check size={22} className="text-emerald-700 mt-1 shrink-0" />
          <div>
            <div className="font-display font-black text-xl">You're on the list.</div>
            <p className="text-sm text-zinc-700 mt-2">
              We'll email you at <strong>{email}</strong> whenever a new Help Identify case is published in: {" "}
              <strong>{selected.join(", ")}</strong>. Every email has a one-click unsubscribe link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border-2 border-zinc-900 bg-zinc-900 text-white p-6" data-testid="active-case-alerts-form">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 border border-red-600 bg-red-600 flex items-center justify-center">
          <Bell size={18} />
        </div>
        <div className="label-eyebrow text-red-500">Get alerted</div>
      </div>
      <div className="font-display font-black text-xl mt-4 leading-tight">
        Email me when a new Help Identify case publishes in my state.
      </div>
      <p className="text-xs text-zinc-300 mt-2 leading-relaxed">
        For shelters, rescues, animal-control staff and members of the public. Each alert includes the case image, location, agency contact and a direct link. One-click unsubscribe in every email.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1 text-zinc-400">Email *</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-red-500"
            placeholder="you@example.com"
            data-testid="active-case-alerts-email"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-zinc-400">I'm a…</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-red-500"
            data-testid="active-case-alerts-role"
          >
            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-zinc-400">Organization (optional)</label>
          <input
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-red-500"
            placeholder="e.g. Austin Animal Center"
            data-testid="active-case-alerts-org"
          />
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <span className="label-eyebrow text-zinc-400">States / regions *</span>
          <span className="text-[11px] font-mono text-zinc-500" data-testid="active-case-alerts-count">
            {selected.length} selected
          </span>
        </div>
        <div className="flex flex-wrap gap-1 max-h-44 overflow-y-auto p-2 border border-zinc-700 bg-zinc-800/40">
          {US_STATES_FULL.map(([code, name]) => {
            const on = selected.includes(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => toggle(code)}
                data-testid={`active-case-alerts-state-${code}`}
                className={`text-[11px] font-mono px-2 py-1 border transition-colors ${
                  on
                    ? "bg-red-600 border-red-500 text-white"
                    : "bg-transparent border-zinc-700 text-zinc-300 hover:border-red-500"
                }`}
                title={name}
              >
                {code}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="active-case-alerts-submit"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-60 transition-colors text-white font-black uppercase tracking-widest text-sm px-5 py-3 border-2 border-red-500"
      >
        {submitting ? <Loader2 size={14} className="animate-spin"/> : <Bell size={14}/>}
        Subscribe to Help Identify alerts
      </button>
      <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
        We'll only use this email to send Active Case alerts for the states you selected. No marketing, no spam.
      </p>
    </form>
  );
}
